import {get} from 'lodash';

const noReapet = (arr) => {
    let name = 'name';
    let map = new Map();
    for (let item of arr) {
        if (!map.has(item.nodeId)) {
            map.set(item.nodeId, item);
        }
    }
    return [...map.values()];
};

// A campaign group: the backend (Invitation.getTreeGroupedByCampaign_) puts one
// group per campaign of the node's owner between the node and its children.
const isCampaignNode = (item) => get(item, 'type') === 'campaign';

// The sorting is done here, on the frontend, and nowhere else:
//  - the campaign groups come before the plain nodes;
//  - between the groups the default one is first, then by name, then by id;
//  - the plain nodes keep the order they had (by name).
const compareNodes = (a, b) => {
    if (a.isCampaign !== b.isCampaign) {
        return a.isCampaign ? -1 : 1;
    }
    if (a.isCampaign) {
        if (a.isDefault !== b.isDefault) {
            return b.isDefault - a.isDefault;
        }
        const aName = a.name || '';
        const bName = b.name || '';
        if (aName !== bName) {
            return aName.localeCompare(bName);
        }
        return String(a.id).localeCompare(String(b.id));
    }
    return a.name.localeCompare(b.name);
};

// A campaign group has no user behind it and cannot be selected; the rate it
// shows is the rate of the campaign itself (0 is a valid rate, so no fallback).
const campaignNode = (item, subChild) => {
    const rate = Number(get(item, 'commissionRate')) || 0;
    const name = get(item, 'name');
    return {
        id: String(get(item, 'nodeId') || get(item, 'id') || ''),
        ref: get(item, 'ref'),
        ibLevel: null,
        adminCommissionRate: rate,
        commissionRate: rate,
        name: name === null || name === undefined ? null : String(name),
        childrenName: name === null || name === undefined ? null : String(name),
        email: null,
        mobile: null,
        subLevels: 20,
        referralType: 'Campaign',
        userId: null,
        disabled: true,
        isTop: false,
        isCampaign: true,
        campaignId: get(item, 'campaignId') === undefined ? null : get(item, 'campaignId'),
        isDefault: Number(get(item, 'isDefault')) || 0,
        status: get(item, 'status') === undefined ? null : get(item, 'status'),
        children: subChild,
        ibCode: get(item, 'ibCode'),
    };
};

export const processTreeView = (child, index = 0) => {
    if (child.length === 0) {
        return;
    }
    child = noReapet(child);

    child = child.map((item) => {
        const isCampaign = isCampaignNode(item);
        let subChild;
        if (item['children']) {
            // The campaign layer does not take a level of its own: the members
            // of a group stay on the level of the group, so they keep the
            // name/abbreviation and the enabled state of that level.
            subChild = processTreeView(
                item['children'] || [],
                isCampaign ? index : index + 1,
            );
        }
        if (isCampaign) {
            return campaignNode(item, subChild);
        }
        let childrenName = get(item, 'name').replace(/\(.*?\)/, '');
        childrenName = childrenName.split(' ');
        return {
            id: (get(item, 'nodeId') || get(item, 'id')) + '',
            ref: get(item, 'ref'),
            ibLevel: get(item, 'parent') === 0 ? 999 : get(item, 'ibLevel'),
            adminCommissionRate:
                index === 0
                    ? 0
                    : get(item, 'ibLevel')
                    ? get(item, 'commissionRate') || 0.2
                    : '',
            commissionRate:
                index === 0
                    ? 0
                    : get(item, 'ibLevel')
                    ? get(item, 'commissionRate') || 0.2
                    : '',
            name: get(item, 'name').replace(/\(.*?\)/, ''),
            childrenName:
                index <= 1
                    ? get(item, 'name')
                    : `${get(childrenName, '[0][0]')?.toUpperCase() || ''}.${
                          get(childrenName, '[1][0]')?.toUpperCase() || ''
                      }.`,
            email: get(item, 'email'),
            mobile: get(item, 'mobile'),
            subLevels: 20,
            referralType: get(item, 'ibLevel') ? 'IB' : 'Client',
            userId: get(item, 'userId'),
            disabled: index === 0 || index > 1,
            isTop: index === 0,
            isCampaign: false,
            children: subChild,
            ibCode: get(item, 'ibCode')
        };
    }).sort(compareNodes);
    return child;
};

/**
 * The direct children of the root of a tree.
 *
 * With the campaign layer the root's children are the campaign groups, so the
 * groups are flattened (one level only) to get the real direct children.
 * Without the campaign layer this is the same as `tree[0].children`.
 *
 * @param {Array} tree the processed tree
 * @returns {Array} the direct children
 */
export const getDirectChildren = (tree) => {
    const rootChildren = get(tree, '[0].children') || [];
    const children = [];
    rootChildren.forEach((node) => {
        if (node && node.isCampaign) {
            (node.children || []).forEach((child) => children.push(child));
        } else {
            children.push(node);
        }
    });
    return children;
};
